import { pointer, drag, Selection, BaseType } from 'd3';
import moment from 'moment';
import { DATE_FORMAT_STRING, TDate } from '@monorepo/types';
import { TDrawModel } from '../../draw.model';
import { TEntityActionCb, TRectElements } from '../../timeline.types';
import { TTimelineChartGroups } from '../draw';
import { DRAW_MODEL_UTILS, TTimelineRowAtY } from '../utils';
import { DRAW_ELEMENT } from './elements';
import { CHART_EVENTS } from './event.tasks';

const DRAG = {
  DRAG_START_THRESHOLD_PX: 5,
  GHOST_WIDTH_PX: 160,
  GHOST_FILL: '#000000',
  GHOST_TEXT_FILL: '#FFFFFF',
  GHOST_BORDER_FILL: '#FFFFFF',
  GHOST_FONT_SIZE: 12,
  GHOST_PADDING_X: 8,
  CHAR_WIDTH_PX: 7,
  GHOST_OPACITY: 0.95,
  GHOST_BLOCKED_OPACITY: 0.4,
  DRAGGED_STRIP_OPACITY: 0.3,
  EDGE_SCROLL_ZONE_PX: 48,
  EDGE_SCROLL_MAX_SPEED_PX: 18,
  HOVERED_ROW_FILL: 'rgba(56, 121, 219, 0.12)',
  NAV_FILL: '#3879db',
  NAV_FILL_OPACITY: 0.25,
  PILL_FILL: '#000000',
  PILL_HEIGHT: 18,
  PILL_FONT_SIZE: 11,
  PILL_PADDING_X: 6,
  PILL_TOP_OFFSET: 4,
  CORNER_RADIUS: 4,
};

const CLASS = {
  ghost: 'strip-drag-ghost',
  hoveredRow: 'strip-drag-hovered-row',
  nav: 'strip-drag-nav',
  pill: 'strip-drag-pill',
};

export type TStripDragOptions = {
  runOutsideAngular: <T>(fn: () => T) => T;
  run: (fn: () => void) => void;
  getScrollEl: () => HTMLDivElement | null;
  scrollBy: (deltaX: number, deltaY: number) => void;
  onDragStateChange: (isDragging: boolean) => void;
};

type TPendingDrag = {
  datum: TRectElements;
  sourceNode: SVGSVGElement;
  origin: TTimelineRowAtY;
  pressedAtClientX: number;
  pressedAtClientY: number;
};

/** Each visual is created once on drag start, then only has its attributes updated */
type TDragVisuals = {
  hoveredRow: (rowIndex: number | null) => void;
  ghost: (x: number, y: number, canDrop: boolean) => void;
  pill: (rowIndex: number | null, centerX: number, label: string) => void;
};

type TDragState = {
  sourceNode: SVGSVGElement;
  visuals: TDragVisuals;
  ghostOffsetYInRow: number;
  sourceRowIndex: number;
  stripStartDay: string;
  entityData: unknown;
  entityGroupData: unknown;
  target: TTimelineRowAtY | null;
  dropDate: TDate | null;
  canDrop: boolean;
  lastEvent: MouseEvent;
  rafId: number | null;
  onKeyDown: (event: KeyboardEvent) => void;
  isCancelled: boolean;
};

const swallowClickAfterRelease = () => {
  const stopClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };
  const onRelease = () => {
    window.addEventListener('click', stopClick, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('click', stopClick, true));
  };

  window.addEventListener('mouseup', onRelease, { capture: true, once: true });
};

const clearDragVisuals = (chartTemplate: TTimelineChartGroups) => {
  chartTemplate.foreground.selectAll(`.${CLASS.ghost}, .${CLASS.hoveredRow}, .${CLASS.pill}`).remove();
  chartTemplate.leftNav?.selectAll(`.${CLASS.nav}`).remove();
};

export const removeStripDrag = (chartTemplate: TTimelineChartGroups) => {
  chartTemplate.elements.on('.drag', null);
  clearDragVisuals(chartTemplate);
};

export const addStripDrag = (
  chartTemplate: TTimelineChartGroups,
  drawModel: TDrawModel,
  stripEvent: TEntityActionCb,
  opts: TStripDragOptions,
) => {
  const bodyNode = chartTemplate.body.node() as SVGSVGElement | null;
  if (!bodyNode) return null;

  const { entityHeight, offsetBottom, widthOfDay, margin, headerHeight, stackHeights } = drawModel.layout;

  let pending: TPendingDrag | null = null;
  let state: TDragState | null = null;

  const rowTopY = (rowIndex: number) => entityHeight * rowIndex;

  const createHoveredRow = (): TDragVisuals['hoveredRow'] => {
    const band = chartTemplate.foreground
      .append('rect')
      .classed(CLASS.hoveredRow, true)
      .attr('x', 0)
      .attr('width', drawModel.innerWidth)
      .attr('height', entityHeight)
      .attr('fill', DRAG.HOVERED_ROW_FILL)
      .style('pointer-events', 'none');

    const nav = chartTemplate.leftNav
      ?.select('.left-nav-background')
      .append('rect')
      .classed(CLASS.nav, true)
      .attr('x', 0)
      .attr('width', margin.left)
      .attr('height', entityHeight)
      .attr('fill', DRAG.NAV_FILL)
      .attr('fill-opacity', DRAG.NAV_FILL_OPACITY)
      .style('pointer-events', 'none');

    return (rowIndex) => {
      const display = rowIndex === null ? 'none' : null;
      const y = rowTopY(rowIndex ?? 0);
      band.attr('display', display).attr('y', y);
      nav?.attr('display', display).attr('y', y);
    };
  };

  const createGhost = (datum: TRectElements): TDragVisuals['ghost'] => {
    const height = stackHeights[datum.stackCount];
    const maxChars = Math.floor((DRAG.GHOST_WIDTH_PX - DRAG.GHOST_PADDING_X * 2) / DRAG.CHAR_WIDTH_PX);
    const label = datum.name.length > maxChars ? `${datum.name.slice(0, maxChars - 1)}…` : datum.name;

    const ghost = chartTemplate.foreground
      .append('svg')
      .classed(CLASS.ghost, true)
      .attr('width', DRAG.GHOST_WIDTH_PX)
      .attr('height', height)
      .style('pointer-events', 'none')
      .style('filter', 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35))');

    // Inset by half the stroke, which the viewport would otherwise clip
    ghost
      .append('rect')
      .attr('x', 0.5)
      .attr('y', 0.5)
      .attr('width', 'calc(100% - 1px)')
      .attr('height', 'calc(100% - 1px)')
      .attr('rx', DRAG.CORNER_RADIUS)
      .attr('fill', DRAG.GHOST_FILL)
      .attr('stroke', DRAG.GHOST_BORDER_FILL)
      .attr('stroke-width', 1);

    ghost
      .append('text')
      .attr('x', DRAG.GHOST_PADDING_X)
      .attr('y', height / 2)
      .attr('dominant-baseline', 'central')
      .attr('font-size', DRAG.GHOST_FONT_SIZE)
      .attr('font-weight', 'bold')
      .attr('fill', DRAG.GHOST_TEXT_FILL)
      .text(label);

    return (x, y, canDrop) =>
      ghost
        .attr('x', x)
        .attr('y', y)
        .attr('opacity', canDrop ? DRAG.GHOST_OPACITY : DRAG.GHOST_BLOCKED_OPACITY);
  };

  const createDatePill = (): TDragVisuals['pill'] => {
    const group = chartTemplate.foreground.append('g').classed(CLASS.pill, true).style('pointer-events', 'none');
    const box = group
      .append('rect')
      .attr('height', DRAG.PILL_HEIGHT)
      .attr('rx', DRAG.PILL_HEIGHT / 2)
      .attr('fill', DRAG.PILL_FILL);
    const text = group
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', DRAG.PILL_FONT_SIZE)
      .attr('fill', DRAG.GHOST_TEXT_FILL);

    let renderedLabel = '';
    let width = 0;

    return (rowIndex, centerX, label) => {
      group.attr('display', rowIndex === null ? 'none' : null);
      if (rowIndex === null) return;

      if (label !== renderedLabel) {
        renderedLabel = label;
        text.text(label);
        width = (text.node()?.getComputedTextLength() ?? 0) + DRAG.PILL_PADDING_X * 2;
      }

      const topY = rowTopY(rowIndex) + DRAG.PILL_TOP_OFFSET;
      box
        .attr('x', centerX - width / 2)
        .attr('y', topY)
        .attr('width', width);
      text.attr('x', centerX).attr('y', topY + DRAG.PILL_HEIGHT / 2);
    };
  };

  const updateFromPointer = (pointerX: number, pointerY: number) => {
    if (!state) return;

    const isWithinTimeline = pointerX >= 0 && pointerX <= drawModel.innerWidth;
    const target = isWithinTimeline ? DRAW_MODEL_UTILS.getRowByYPosition(pointerY, drawModel) : null;
    const dropDate = DRAW_MODEL_UTILS.getDateByXPosition(pointerX, drawModel);
    const dayDelta = moment.utc(dropDate).diff(moment.utc(state.stripStartDay), 'days');

    const isUnmoved = !!target && target.overallRowIndex === state.sourceRowIndex && dayDelta === 0;
    const canDrop = !!target && !isUnmoved && target.entity.isStripDropTarget !== false;

    state.target = target;
    state.dropDate = dropDate;
    state.canDrop = canDrop;

    const rowIndex = canDrop && target ? target.overallRowIndex : null;
    const ghostX = DRAW_MODEL_UTILS.getPostionByDate(dropDate, drawModel) - widthOfDay / 2;
    const deltaLabel = dayDelta === 0 ? '' : ` · ${dayDelta > 0 ? '+' : ''}${dayDelta}d`;

    state.visuals.hoveredRow(rowIndex);
    state.visuals.ghost(
      ghostX,
      rowTopY(target ? target.overallRowIndex : state.sourceRowIndex) + state.ghostOffsetYInRow,
      canDrop,
    );
    state.visuals.pill(
      rowIndex,
      ghostX + Math.min(widthOfDay, DRAG.GHOST_WIDTH_PX) / 2,
      `${moment.utc(dropDate).format('D MMM')}${deltaLabel}`,
    );

    document.body.style.cursor = canDrop ? 'grabbing' : 'not-allowed';
  };

  const refreshFromLastEvent = () => {
    if (!state) return;
    const [x, y] = pointer(state.lastEvent, bodyNode);
    updateFromPointer(x, y);
  };

  const edgeScrollDelta = (position: number, nearEdge: number, farEdge: number) => {
    const { EDGE_SCROLL_ZONE_PX: zone, EDGE_SCROLL_MAX_SPEED_PX: maxSpeed } = DRAG;
    if (farEdge - nearEdge < zone * 2) return 0;
    if (position < nearEdge + zone) return -Math.min(1, (nearEdge + zone - position) / zone) * maxSpeed;
    if (position > farEdge - zone) return Math.min(1, (position - (farEdge - zone)) / zone) * maxSpeed;
    return 0;
  };

  const tickAutoScroll = () => {
    if (!state) return;
    state.rafId = requestAnimationFrame(tickAutoScroll);

    const scrollEl = opts.getScrollEl();
    if (!scrollEl) return;

    const rect = scrollEl.getBoundingClientRect();
    const deltaX = edgeScrollDelta(state.lastEvent.clientX, rect.left + margin.left, rect.right);
    const deltaY = edgeScrollDelta(state.lastEvent.clientY, rect.top + headerHeight, rect.bottom);
    if (deltaX === 0 && deltaY === 0) return;

    const { scrollLeft, scrollTop } = scrollEl;
    opts.scrollBy(deltaX, deltaY);
    if (scrollEl.scrollLeft !== scrollLeft || scrollEl.scrollTop !== scrollTop) refreshFromLastEvent();
  };

  const cancelDrag = () => {
    pending = null;
    if (!state) return;
    state.isCancelled = true;
    window.dispatchEvent(new MouseEvent('mouseup', { view: window, bubbles: true }));
    swallowClickAfterRelease();
  };

  const teardown = () => {
    if (!state) return;
    if (state.rafId !== null) cancelAnimationFrame(state.rafId);
    window.removeEventListener('keydown', state.onKeyDown, true);
    state.sourceNode.removeAttribute('opacity');
    document.body.style.cursor = '';
    clearDragVisuals(chartTemplate);
    CHART_EVENTS.stripToolTip(chartTemplate, drawModel);
  };

  const beginDrag = ({ datum, sourceNode, origin }: TPendingDrag, sourceEvent: MouseEvent) => {
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape') return;
      keyEvent.preventDefault();
      cancelDrag();
    };

    DRAW_ELEMENT.removeToolTip();
    chartTemplate.elements.on('mousemove.tooltip', null).on('mouseout.tooltip', null);

    state = {
      sourceNode,
      // Created in paint order: the ghost and pill must sit above the row band
      visuals: { hoveredRow: createHoveredRow(), ghost: createGhost(datum), pill: createDatePill() },
      ghostOffsetYInRow:
        DRAW_MODEL_UTILS.calculateElementYPosition({ ...datum, heightInPercent: 100 }, drawModel) - offsetBottom,
      sourceRowIndex: origin.overallRowIndex,
      stripStartDay: moment.utc(datum.range.startDate).format(DATE_FORMAT_STRING),
      entityData: origin.entity.data,
      entityGroupData: origin.entityGroup.data,
      target: origin,
      dropDate: null,
      canDrop: false,
      lastEvent: sourceEvent,
      rafId: null,
      onKeyDown,
      isCancelled: false,
    };

    sourceNode.setAttribute('opacity', String(DRAG.DRAGGED_STRIP_OPACITY));
    window.addEventListener('keydown', onKeyDown, true);

    opts.onDragStateChange(true);
    state.rafId = requestAnimationFrame(tickAutoScroll);

    opts.run(() =>
      stripEvent({
        type: 'STRIP_DRAG',
        action: 'START',
        event: sourceEvent,
        elementData: datum.data,
        entityData: origin.entity.data,
        entityGroupData: origin.entityGroup.data,
      }),
    );
  };

  const behaviour = drag<SVGSVGElement, TRectElements>()
    .container(() => bodyNode)
    .subject((event: { x: number; y: number }) => ({ x: event.x, y: event.y }))
    .clickDistance(DRAG.DRAG_START_THRESHOLD_PX)
    .filter(
      (event: MouseEvent, d) =>
        event.button === 0 && !event.ctrlKey && d.type === 'STRIP' && !!d.isDraggable && !!d.data,
    )
    .on('start', function (event, d) {
      const origin = DRAW_MODEL_UTILS.getRowByYPosition(event.y, drawModel);
      const sourceEvent = event.sourceEvent as MouseEvent;
      if (!origin) return;

      pending = {
        datum: d,
        sourceNode: this as SVGSVGElement,
        origin,
        pressedAtClientX: sourceEvent.clientX,
        pressedAtClientY: sourceEvent.clientY,
      };
    })
    .on('drag', function (event) {
      const sourceEvent = event.sourceEvent as MouseEvent;

      if (!state) {
        if (!pending) return;
        const travelled = Math.hypot(
          sourceEvent.clientX - pending.pressedAtClientX,
          sourceEvent.clientY - pending.pressedAtClientY,
        );
        if (travelled < DRAG.DRAG_START_THRESHOLD_PX) return;
        beginDrag(pending, sourceEvent);
      }

      if (!state) return;
      state.lastEvent = sourceEvent;
      updateFromPointer(event.x, event.y);
    })
    .on('end', function (event, d) {
      pending = null;
      if (!state) return; // never passed the threshold, so it was a click

      const { isCancelled, canDrop, target, dropDate, entityData, entityGroupData } = state;

      teardown();
      state = null;
      opts.onDragStateChange(false);

      const baseEvent = {
        type: 'STRIP_DRAG',
        event: event.sourceEvent as Event,
        elementData: d.data,
        entityData,
        entityGroupData,
      } as const;

      opts.run(() =>
        stripEvent(
          !isCancelled && canDrop && target && dropDate
            ? {
                ...baseEvent,
                action: 'DROP',
                date: dropDate,
                targetEntityData: target.entity.data,
                targetEntityGroupData: target.entityGroup.data,
              }
            : { ...baseEvent, action: 'CANCEL' },
        ),
      );
    });

  opts.runOutsideAngular(() => {
    (chartTemplate.elements as unknown as Selection<SVGSVGElement, TRectElements, BaseType, unknown>).call(behaviour);
  });

  return { cancel: cancelDrag };
};

export type TStripDragHandle = ReturnType<typeof addStripDrag>;
